import api from "api/api";
import SockJS from "sockjs-client";
import { Client, Subscription, Message } from "stompjs";
var Stomp = require("stompjs/lib/stomp.js").Stomp;

type ConnectionStatus = "connected" | "connecting" | "disconnected";

type TopicHandler = {
  handler: (message: Message) => void;
  subscription: Subscription | null;
};

class WebSocketService {
  private connection: { stompClient: Client; status: ConnectionStatus } | null =
    null;
  private subscriptions: Map<string, TopicHandler> = new Map();
  private reconnectDelay = 10000;
  private heartbeatInterval = 12 * 60 * 60 * 1000;
  private retryAttempts = 0;
  private maxRetryAttempts = 3;

  constructor(private serverUrl: string) {
    this.connect();
  }

  private handleError(error: any, context: string): void {
    console.error(`>> WebSocket Error in ${context}:`, error);
  }

  public getConnectionStatus(): ConnectionStatus {
    return this.connection?.status || "disconnected";
  }

  public connect(): void {
    if (this.connection && this.connection.stompClient.connected) return;

    console.log(`>> Connecting to WebSocket: ${this.serverUrl}`);

    try {
      const sock = new SockJS(this.serverUrl);
      const stompClient = Stomp.over(sock);
      stompClient.debug = () => {};
      stompClient.heartbeat.incoming = this.heartbeatInterval;
      stompClient.heartbeat.outgoing = this.heartbeatInterval;

      stompClient.connect(
        {},
        () => {
          console.log(`>> Connected to WebSocket: ${this.serverUrl}`);
          this.retryAttempts = 0;
          this.connection = { stompClient, status: "connected" };
          this.processQueuedSubscriptions();
        },
        (error: any) => {
          this.handleError(error, "connect()");
          this.connection = { stompClient, status: "disconnected" };

          if (this.retryAttempts < this.maxRetryAttempts) {
            this.retryAttempts++;
            console.log(
              `>> Retrying connection... Attempt ${this.retryAttempts}`
            );
            setTimeout(() => this.connect(), this.reconnectDelay);
          } else {
            console.error(
              `>> Max retry attempts reached for ${this.serverUrl}.`
            );
          }
        }
      );
    } catch (error) {
      this.handleError(error, "connect()");
    }
  }

  private processQueuedSubscriptions(): void {
    const stompClient = this.connection?.stompClient;
    if (!stompClient || !stompClient.connected) return;

    this.subscriptions.forEach((entry, topic) => {
      console.log(`>> Resubscribing to queued topic: ${topic}`);
      const subscription = stompClient.subscribe(topic, entry.handler);
      entry.subscription = subscription;
    });
  }

  public subscribe(
    topic: string,
    handleMessage: (message: Message) => void
  ): void {
    const stompClient = this.connection?.stompClient;

    if (stompClient && stompClient.connected) {
      try {
        const subscription = stompClient.subscribe(topic, handleMessage);
        this.subscriptions.set(topic, { handler: handleMessage, subscription });
        console.log(`>> Subscribed to ${topic}`);
      } catch (error) {
        this.handleError(error, `subscribe(${topic})`);
      }
    } else {
      console.log(`>> WebSocket not connected. Queuing ${topic}`);
      this.subscriptions.set(topic, {
        handler: handleMessage,
        subscription: null,
      });
    }
  }

  public unsubscribe(topic: string): void {
    try {
      const subData = this.subscriptions.get(topic);
      if (subData?.subscription) {
        subData.subscription.unsubscribe();
        this.subscriptions.delete(topic);
        console.log(`>> Unsubscribed from ${topic}`);
      }
    } catch (error) {
      this.handleError(error, `unsubscribe(${topic})`);
    }
  }

  public unsubscribeAll(): void {
    try {
      this.subscriptions.forEach((entry, topic) => {
        try {
          entry.subscription?.unsubscribe();
          console.log(`>> Unsubscribed from ${topic}`);
        } catch (error) {
          this.handleError(error, `unsubscribeAll(${topic})`);
        }
      });
      this.subscriptions.clear();
    } catch (error) {
      this.handleError(error, "unsubscribeAll()");
    }
  }

  public disconnect(): void {
    if (this.connection?.stompClient && this.connection.stompClient.connected) {
      this.connection.stompClient.disconnect(() => {
        console.log(`>> Disconnected from WebSocket: ${this.serverUrl}`);
      });
    }
    this.connection = null;
    this.subscriptions.clear();
  }
}

const MimicServerSocket = new WebSocketService(api.getWebSocketApiUrl());

export { MimicServerSocket };
